// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { PencilSimple } from "@phosphor-icons/react";
import { Button, IconButton, Text, Tooltip } from "@radix-ui/themes";
import { type KeyboardEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Header control on the caller's own human comment. Hidden until hover or
 * focus on a closed card, like the judgment icons on an AI finding.
 */
export function AskV2ReviewCommentEditButton({
  commentId,
  pinned,
  onRequestEdit,
}: {
  commentId: string;
  /** Keep it visible without hover: the open card. */
  pinned: boolean;
  onRequestEdit: () => void;
}) {
  const { t } = useTranslation("ui");
  const label = t("askV2.review.editComment");
  return (
    <div
      className={`shrink-0 transition-opacity ${
        pinned
          ? ""
          : "opacity-0 focus-within:opacity-100 group-hover/comment:opacity-100"
      }`}
    >
      <Tooltip content={label}>
        <IconButton
          type="button"
          size="1"
          variant="soft"
          aria-label={label}
          className="text-puntt-neutral-gray-11"
          data-testid={`ask-v2-review-comment-edit-${commentId}`}
          onClick={onRequestEdit}
        >
          <PencilSimple height="16" width="16" aria-hidden="true" />
        </IconButton>
      </Tooltip>
    </div>
  );
}

/**
 * Inline editor for a comment's text. Save is offered only when the text is
 * non-blank and differs from what is saved; Escape cancels and Cmd/Ctrl+Enter
 * saves.
 */
export function AskV2ReviewCommentEditor({
  commentId,
  saved,
  initialDraft,
  failed,
  onSave,
  onCancel,
}: {
  commentId: string;
  /** The comment's text as it stands, to tell an unchanged draft apart. */
  saved: string;
  /** What the box opens with: the saved text, or a draft a failed save kept. */
  initialDraft: string;
  /** The last save did not land; the draft is kept so nothing is lost. */
  failed: boolean;
  onSave: (description: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation("ui");
  const inputId = useId();
  const [draft, setDraft] = useState(initialDraft);
  const next = draft.trim();
  const canSave = next.length > 0 && next !== saved.trim();

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && canSave) {
      event.preventDefault();
      onSave(next);
    }
  };

  return (
    <form
      className="mt-2 flex flex-col gap-2"
      data-testid={`ask-v2-review-comment-editor-${commentId}`}
      aria-label={t("askV2.review.editComment")}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) onSave(next);
      }}
    >
      <label htmlFor={inputId} className="sr-only">
        {t("askV2.review.editCommentInputLabel")}
      </label>
      <textarea
        id={inputId}
        // why: the editor opens because she asked to edit; focus lands in the box
        // so typing starts at once, the same as the add-comment composer.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        rows={3}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full resize-y rounded-md border border-puntt-neutral-gray-6 bg-base-white p-2 text-[14px] text-puntt-neutral-gray-12 focus:border-puntt-blue-9 focus:outline-none"
        data-testid={`ask-v2-review-comment-editor-input-${commentId}`}
      />
      {failed ? (
        <Text
          as="p"
          size="2"
          color="red"
          role="alert"
          data-testid={`ask-v2-review-comment-editor-error-${commentId}`}
        >
          {t("askV2.review.editCommentFailed")}
        </Text>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="1"
          variant="soft"
          color="gray"
          onClick={onCancel}
          data-testid={`ask-v2-review-comment-editor-cancel-${commentId}`}
        >
          {t("askV2.review.editCommentCancel")}
        </Button>
        <Button
          type="submit"
          size="1"
          disabled={!canSave}
          data-testid={`ask-v2-review-comment-editor-save-${commentId}`}
        >
          {t("askV2.review.editCommentSave")}
        </Button>
      </div>
    </form>
  );
}
